import { useState, useEffect } from 'react'
import { Download, X, Smartphone } from 'lucide-react'

export default function InstallButton() {
  const [promptEvent, setPromptEvent] = useState(null)
  const [showIOSGuide, setShowIOSGuide] = useState(false)
  // 렌더 전에 확정되는 값은 lazy init으로 — effect 안 동기 setState 금지 룰 준수.
  const [isIOS] = useState(() => /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream)
  // iOS 비-Safari 브라우저: 공유 버튼 위치가 다름 (iOS 16.4+면 설치 자체는 가능)
  const [isIOSNonSafari] = useState(() =>
    /CriOS|FxiOS|EdgiOS|OPiOS|OPT\/|Whale|YaBrowser/i.test(navigator.userAgent)
  )
  // 이미 standalone(설치됨) 상태면 버튼 숨김
  const [installed, setInstalled] = useState(
    () => window.matchMedia('(display-mode: standalone)').matches
  )

  useEffect(() => {
    if (installed) return undefined

    const handler = (e) => {
      e.preventDefault()
      setPromptEvent(e)
    }
    const onInstalled = () => setInstalled(true)
    window.addEventListener('beforeinstallprompt', handler)
    window.addEventListener('appinstalled', onInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', handler)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [installed])

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSGuide(true)
      return
    }
    if (!promptEvent) return
    promptEvent.prompt()
    const { outcome } = await promptEvent.userChoice
    if (outcome === 'accepted') setInstalled(true)
    setPromptEvent(null)
  }

  // 설치됨이거나, Android인데 프롬프트도 없으면 숨김
  if (installed) return null
  if (!isIOS && !promptEvent) return null

  return (
    <>
      <button
        onClick={handleInstall}
        className="flex items-center gap-1 text-xs text-violet-600 hover:text-violet-800 border border-violet-300 rounded-lg px-2 py-1.5 flex-shrink-0 font-medium"
        title="앱 설치"
      >
        <Download size={13} />
        <span>앱 설치</span>
      </button>

      {showIOSGuide && (
        <div className="fixed inset-0 z-50 flex items-end justify-center px-4 pb-6">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowIOSGuide(false)} />
          <div className="relative z-10 w-full max-w-sm bg-white rounded-2xl shadow-xl p-5 flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Smartphone size={18} className="text-violet-600" />
                <h2 className="font-bold text-gray-900 text-base">홈 화면에 추가하기</h2>
              </div>
              <button onClick={() => setShowIOSGuide(false)} className="text-gray-400 hover:text-gray-600">
                <X size={18} />
              </button>
            </div>
            <ol className="flex flex-col gap-3 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center mt-0.5">1</span>
                <span>
                  {isIOSNonSafari
                    ? '주소창 오른쪽의 '
                    : 'Safari 하단의 '}
                  <strong>공유 버튼(↑)</strong>을 탭하세요.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center mt-0.5">2</span>
                <span>스크롤해서 <strong>"홈 화면에 추가"</strong>를 탭하세요.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center mt-0.5">3</span>
                <span>오른쪽 위 <strong>"추가"</strong>를 탭하면 완료!</span>
              </li>
            </ol>
            <p className="text-xs text-gray-400 text-center">
              * "홈 화면에 추가"가 안 보이면(iOS 16.4 미만) Safari로 접속해 주세요.
            </p>
          </div>
        </div>
      )}
    </>
  )
}
